import { useEffect, useState } from "react";
import { api, brl, API } from "../lib/api";
import { AdminGate, AdminShell } from "./AdminGate";
import { connectWS } from "../lib/ws";
import { toast } from "sonner";
import { CheckCircle2, XCircle, MapPin, RefreshCw } from "lucide-react";
import { ChatCorrida } from "./DriverApp";

const MOTIVOS_RETIRADA = ["Motorista não poderá realizar a corrida", "Motorista não está respondendo", "Motorista informou indisponibilidade", "Problema operacional", "Descumprimento de orientação", "Conduta inadequada", "Desrespeito", "Outro motivo"];

function RetirarMotoristaModal({ job, onClose, onDone }) {
  const [motivo, setMotivo] = useState("");
  const [obs, setObs] = useState("");
  const [busy, setBusy] = useState(false);
  const confirmar = async () => {
    if (!window.confirm(`Retirar ${job.motorista_nome} desta corrida? A corrida do cliente continuará ATIVA e voltará para distribuição.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/admin/jobs/${job.job_id}/retirar-motorista`, { motivo, observacao: obs });
      toast.success(`Motorista retirado. Corrida reaberta — enviada para ${r.data.redistribuida_para} motorista(s) elegível(is).`);
      onClose();
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" data-testid="retirar-motorista-modal">
      <div className="bg-white w-full max-w-md rounded-t-3xl sm:rounded-3xl p-5 space-y-3">
        <h3 className="font-heading font-extrabold text-navy text-base">🔄 Retirar motorista da corrida</h3>
        <p className="text-[11px] text-slate-500">Motorista atual: <strong>{job.motorista_nome}</strong>. A corrida do cliente NÃO será cancelada — ela voltará para distribuição com status AGUARDANDO NOVO MOTORISTA.</p>
        <div className="grid grid-cols-2 gap-1.5">
          {MOTIVOS_RETIRADA.map((m) => (
            <button key={m} data-testid={`retirada-motivo-${m}`} onClick={() => setMotivo(m)}
              className={`py-2 px-1 rounded-lg border-2 text-[10px] font-bold active:scale-95 transition-all ${motivo === m ? "border-navy bg-slate-100 text-navy" : "border-slate-200 text-slate-500"}`}>
              {m}
            </button>
          ))}
        </div>
        <textarea data-testid="retirada-obs" className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm" rows={2}
          placeholder="Observação complementar (opcional)" value={obs} onChange={(e) => setObs(e.target.value)} />
        <button data-testid="retirada-confirmar-btn" onClick={confirmar} disabled={!motivo || busy}
          className="w-full py-3.5 rounded-2xl bg-navy text-[#F5B301] font-heading font-extrabold text-xs active:scale-95 transition-transform disabled:opacity-50">
          {busy ? "Processando..." : "🔄 RETIRAR E ABRIR PARA OUTRO MOTORISTA"}
        </button>
        <button onClick={onClose} className="w-full py-2.5 rounded-2xl bg-slate-100 text-slate-500 font-heading font-bold text-xs active:scale-95 transition-transform">
          VOLTAR
        </button>
      </div>
    </div>
  );
}

const STATUS_BADGE = {
  disponivel: "bg-blue-100 text-blue-700", aceita: "bg-amber-100 text-amber-700",
  a_caminho: "bg-amber-100 text-amber-700", chegou_origem: "bg-amber-100 text-amber-700",
  carga_iniciada: "bg-amber-100 text-amber-700", em_rota: "bg-indigo-100 text-indigo-700",
  chegou_destino: "bg-indigo-100 text-indigo-700", concluida: "bg-emerald-100 text-emerald-700",
  cancelada: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  disponivel: "DISPONÍVEL", aceita: "ACEITA", a_caminho: "A CAMINHO", chegou_origem: "CHEGOU NA ORIGEM",
  carga_iniciada: "CARGA INICIADA", em_rota: "EM ROTA", chegou_destino: "CHEGOU AO DESTINO",
  concluida: "CONCLUÍDA", cancelada: "CANCELADA",
};
const fmtD = (s) => (s ? s.split("-").reverse().join("/") : "-");
const LOC_LABEL = { ativa: "🟢 LOCALIZAÇÃO ATIVA", ultima: "🟡 ÚLTIMA LOCALIZAÇÃO", indisponivel: "🔴 LOCALIZAÇÃO INDISPONÍVEL" };

function Corridas({ user }) {
  const [jobs, setJobs] = useState(null);
  const [locs, setLocs] = useState([]);
  const [subs, setSubs] = useState([]);
  const [chatJob, setChatJob] = useState(null);
  const [retirarJob, setRetirarJob] = useState(null);
  const [reviewUrl, setReviewUrl] = useState("");

  useEffect(() => {
    api.get("/admin/settings").then((r) => setReviewUrl(r.data.google_place_id || "")).catch(() => {});
  }, []);

  const load = () => {
    api.get("/admin/jobs").then((r) => setJobs(r.data.jobs)).catch(() => setJobs([]));
    api.get("/admin/locations").then((r) => setLocs(r.data.locations)).catch(() => {});
    api.get("/admin/substituicoes").then((r) => setSubs(r.data.substituicoes)).catch(() => {});
  };
  useEffect(() => {
    load();
    const i = setInterval(load, 30000);
    const closeWs = connectWS((ev) => {
      if (["corrida_aceita", "corrida_recusada", "status_corrida", "recebimento_confirmado", "corrida_disponibilizada", "alerta_substituicao", "alerta_suporte", "chat_msg"].includes(ev.tipo)) load();
    });
    return () => { clearInterval(i); closeWs(); };
  }, []);

  const confirmarRecebimento = async (id) => {
    if (!window.confirm("Confirmar o recebimento/conclusão deste serviço?")) return;
    try { await api.post(`/admin/jobs/${id}/confirmar-recebimento`); toast.success("Recebimento confirmado pelo ADM"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  const pagar = async (id) => {
    try { await api.post(`/admin/jobs/${id}/pagar`); toast.success("Pagamento do motorista registrado!"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };
  const cancelar = async (id) => {
    if (!window.confirm("Cancelar esta corrida?")) return;
    try { await api.post(`/admin/jobs/${id}/cancelar`); toast.success("Corrida cancelada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
  };

  return (
    <AdminShell user={user} title="Corridas e Motoristas">
      <div className="px-4 mt-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid="admin-locations">
          <div className="flex items-center justify-between mb-2">
            <p className="text-xs font-bold tracking-wider uppercase text-slate-400">📍 Localização dos motoristas</p>
            <button onClick={load} data-testid="locations-refresh" className="p-1.5 text-slate-400 hover:text-navy"><RefreshCw className="w-4 h-4" /></button>
          </div>
          {locs.length === 0 && <p className="text-xs text-slate-400" data-testid="locations-empty">Nenhum motorista em serviço no momento.</p>}
          {locs.map((l) => (
            <div key={l.job_id} className="border-t border-slate-50 py-2.5 first:border-0" data-testid={`loc-row-${l.job_id}`}>
              <div className="flex justify-between items-center">
                <p className="text-xs font-bold text-navy">{l.motorista_nome}</p>
                <span className="text-[10px] font-bold" data-testid={`loc-status-${l.job_id}`}>{LOC_LABEL[l.loc_status]}</span>
              </div>
              <p className="text-[11px] text-slate-500">{fmtD(l.data)} {l.horario} · {STATUS_LABEL[l.status]} · → {l.regiao_destino}</p>
              {l.localizacao && (
                <>
                  <p className="text-[10px] text-slate-400">Última atualização: {new Date(l.localizacao.at).toLocaleTimeString("pt-BR")}</p>
                  <iframe
                    title="mapa"
                    data-testid={`loc-map-${l.job_id}`}
                    className="w-full h-40 rounded-xl mt-1.5 border border-slate-200"
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${l.localizacao.lng - 0.01},${l.localizacao.lat - 0.01},${l.localizacao.lng + 0.01},${l.localizacao.lat + 0.01}&layer=mapnik&marker=${l.localizacao.lat},${l.localizacao.lng}`}
                  />
                  <a className="text-[10px] text-navy font-bold underline" target="_blank" rel="noopener noreferrer"
                    href={`https://www.google.com/maps/search/?api=1&query=${l.localizacao.lat},${l.localizacao.lng}`}>
                    Abrir no Google Maps
                  </a>
                </>
              )}
            </div>
          ))}
          <div className="flex justify-center gap-3 mt-3 pt-2 border-t border-slate-50 text-[10px] font-semibold text-slate-500" data-testid="locations-legend">
            <span>🟢 Ativa (&lt;3 min)</span>
            <span>🟡 Última conhecida</span>
            <span>🔴 Indisponível</span>
          </div>
        </div>

        {subs.length > 0 && (
          <div className="bg-red-50 border-2 border-red-300 rounded-2xl p-4" data-testid="substituicoes-card">
            <p className="font-heading font-extrabold text-red-700 text-sm">🚨 SUBSTITUIÇÕES PENDENTES — {subs.length} corrida(s) precisam de motorista</p>
            <div className="mt-2 space-y-1.5">
              {subs.map((s) => (
                <button key={s.job_id} data-testid={`substituicao-${s.job_id}`}
                  onClick={() => document.querySelector(`[data-testid="admin-job-${s.job_id}"]`)?.scrollIntoView({ behavior: "smooth", block: "center" })}
                  className="w-full text-left bg-white rounded-xl border border-red-100 p-2.5 active:scale-95 transition-transform">
                  <div className="flex justify-between items-center gap-2">
                    <p className="text-[11px] font-bold text-navy">{fmtD(s.data)} {s.horario} · {s.regiao_origem} → {s.regiao_destino}</p>
                    <span className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full shrink-0 ${s.ultima_hora ? "bg-red-600 text-white" : "bg-amber-100 text-amber-700"}`}>
                      {s.ultima_hora ? `🚨 ÚLTIMA HORA (${s.horas_restantes}h)` : "PENDENTE"}
                    </span>
                  </div>
                  <p className="text-[10px] text-slate-500 mt-0.5">{s.origem_situacao} · receberam: {s.enviada_para.length} · recusaram: {s.recusas.length}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {chatJob && (
          <ChatCorrida jobId={chatJob.id} role="adm" token={null} onClose={() => setChatJob(null)} subtitulo={`Supervisão ADM · ${chatJob.sub}`} />
        )}
        {retirarJob && (
          <RetirarMotoristaModal job={retirarJob} onClose={() => setRetirarJob(null)} onDone={load} />
        )}

        <div className="space-y-3" data-testid="jobs-list">
          {jobs === null && <div className="flex justify-center py-14"><div className="w-8 h-8 border-4 border-[#F5B301] border-t-transparent rounded-full animate-spin" /></div>}
          {jobs !== null && jobs.length === 0 && (
            <p className="text-center text-sm text-slate-400 py-14" data-testid="jobs-empty">
              Nenhuma corrida disponibilizada. Abra um orçamento e use "Disponibilizar para motoristas".
            </p>
          )}
          {(jobs || []).map((j) => (
            <div key={j.job_id} className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4" data-testid={`admin-job-${j.job_id}`}>
              <div className="flex items-center justify-between mb-1.5">
                <span className="font-heading font-bold text-navy text-sm">{j.tipo === "frete" ? "🚚 Frete" : "🏠 Mudança"} · {fmtD(j.data)} {j.horario}</span>
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${STATUS_BADGE[j.status]}`} data-testid={`admin-job-status-${j.job_id}`}>{STATUS_LABEL[j.status]}</span>
              </div>
              <p className="text-xs text-slate-600">{j.regiao_origem} → {j.regiao_destino}</p>
              <p className="text-xs text-slate-600 mt-1">Cliente: {j.cliente?.nome} · {j.cliente?.telefone}</p>
              <p className="text-xs text-slate-600">Motorista: <strong>{j.motorista_nome || "— aguardando aceite"}</strong></p>
              <p className="text-xs text-slate-600">Valor ao motorista: <strong className="text-navy">{brl(j.valor_motorista)}</strong> · Pagamento:{" "}
                <strong className={j.pagamento_motorista?.status === "realizado" ? "text-emerald-600" : "text-amber-600"} data-testid={`admin-job-pagamento-${j.job_id}`}>
                  {j.pagamento_motorista?.status === "realizado" ? "REALIZADO" : "PENDENTE"}
                </strong>
              </p>
              {(j.recusas || []).length > 0 && (
                <p className="text-[10px] text-red-400 mt-1">Recusas: {j.recusas.map((r) => r.motorista_nome).join(", ")}</p>
              )}
              <div className="flex gap-1.5 mt-2.5 flex-wrap">
                {j.motorista_id && (
                  <button data-testid={`admin-job-chat-${j.job_id}`} onClick={() => setChatJob({ id: j.job_id, sub: `${j.regiao_origem} → ${j.regiao_destino} · ${fmtD(j.data)} ${j.horario}` })}
                    className="px-3 py-2 rounded-lg bg-navy text-[#F5B301] text-[10px] font-bold active:scale-95 transition-transform">
                    💬 VER CHAT
                  </button>
                )}
                {j.motorista_id && !["concluida", "cancelada"].includes(j.status) && (
                  <button data-testid={`admin-job-retirar-${j.job_id}`} onClick={() => setRetirarJob(j)}
                    className="px-3 py-2 rounded-lg bg-amber-500 text-white text-[10px] font-bold active:scale-95 transition-transform">
                    🔄 RETIRAR MOTORISTA
                  </button>
                )}
                {j.motorista_id && j.pagamento_motorista?.status !== "realizado" && (
                  <button data-testid={`admin-job-pagar-${j.job_id}`} onClick={() => pagar(j.job_id)}
                    className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-[10px] font-bold flex items-center gap-1 active:scale-95 transition-transform">
                    <CheckCircle2 className="w-3.5 h-3.5" /> PAGAMENTO REALIZADO
                  </button>
                )}
                {!["concluida", "cancelada"].includes(j.status) && (
                  <button data-testid={`admin-job-editar-valor-${j.job_id}`} onClick={async () => {
                    const v = window.prompt("Novo valor de repasse ao motorista (R$):", j.valor_motorista);
                    if (v === null) return;
                    const num = parseFloat(String(v).replace(",", "."));
                    if (isNaN(num) || num <= 0) return toast.error("Valor inválido");
                    try { await api.patch(`/admin/jobs/${j.job_id}`, { valor_motorista: num }); toast.success("Valor de repasse atualizado!"); load(); }
                    catch (e) { toast.error(e.response?.data?.detail || "Falha"); }
                  }} className="px-3 py-2 rounded-lg bg-gold-light border border-[#F5B301]/60 text-navy text-[10px] font-bold active:scale-95 transition-transform">
                    ✏️ EDITAR REPASSE
                  </button>
                )}
                {!["concluida", "cancelada"].includes(j.status) && (
                  <button data-testid={`admin-job-cancelar-${j.job_id}`} onClick={() => cancelar(j.job_id)}
                    className="px-3 py-2 rounded-lg bg-red-100 text-red-600 text-[10px] font-bold flex items-center gap-1 active:scale-95 transition-transform">
                    <XCircle className="w-3.5 h-3.5" /> CANCELAR
                  </button>
                )}
                {j.status === "concluida" && !j.recebimento?.confirmado && (
                  <button data-testid={`admin-job-recebimento-${j.job_id}`} onClick={() => confirmarRecebimento(j.job_id)}
                    className="px-3 py-2 rounded-lg bg-navy text-[#F5B301] text-[10px] font-bold flex items-center gap-1 active:scale-95 transition-transform">
                    <CheckCircle2 className="w-3.5 h-3.5" /> CONFIRMAR RECEBIMENTO DO SERVIÇO
                  </button>
                )}
                {j.status === "concluida" && j.recebimento?.confirmado && (
                  <span className="px-3 py-2 rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-700 text-[10px] font-bold" data-testid={`admin-job-recebimento-ok-${j.job_id}`}>
                    ✅ RECEBIMENTO CONFIRMADO {j.recebimento.por === "adm" ? "PELO ADM" : "PELO MOTORISTA"} · {new Date(j.recebimento.em).toLocaleString("pt-BR")}
                  </span>
                )}
                {j.status === "concluida" && reviewUrl && j.cliente?.telefone && (
                  <a data-testid={`admin-job-pedir-avaliacao-${j.job_id}`} target="_blank" rel="noopener noreferrer"
                    href={`https://wa.me/${(() => { const d = (j.cliente.telefone || "").replace(/\D/g, ""); return d.length <= 11 ? "55" + d : d; })()}?text=${encodeURIComponent(`Olá ${j.cliente.nome}! Foi um prazer realizar seu ${j.tipo === "frete" ? "frete" : "mudança"}. Sua avaliação é muito importante para nós: ${reviewUrl}`)}`}
                    className="px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-[10px] font-bold active:scale-95 transition-transform">
                    ⭐ PEDIR AVALIAÇÃO (WHATSAPP)
                  </a>
                )}
                <a data-testid={`admin-job-quote-${j.job_id}`} href={`/admin/orcamento/${j.quote_id}`}
                  className="px-3 py-2 rounded-lg bg-slate-100 text-slate-600 text-[10px] font-bold">VER ORÇAMENTO</a>
              </div>
              {(j.historico || []).length > 0 && (
                <div className="mt-2 pt-2 border-t border-slate-50">
                  {j.historico.slice(-4).map((h, i) => (
                    <p key={i} className="text-[10px] text-slate-400">{h.acao.replace(/_/g, " ")} · {h.admin} · {new Date(h.em).toLocaleString("pt-BR")}</p>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </AdminShell>
  );
}

export default function AdminJobs() {
  return <AdminGate>{(user) => <Corridas user={user} />}</AdminGate>;
}
